<script lang="ts">
	//UI
	import {
		Card,
		ProductComposite,
		DefaultButton,
		TextContentArea,
		DefaultCheckbox,
		DefaultSwitch,
		PopperIcon,
	} from "@hitit/new_ui";
	import AreYouSure from "../../common/AreYouSure.svelte";
	//Utils
	import jsbarcode from "jsbarcode";
	import { TranslationDTO } from "../../../class/TranslationDTO";
	import { popup } from "../../../store";
	import { t } from "@hitit/hub/src/stores";
	import { createEventDispatcher } from "svelte";
	import {
		findTrnaslation,
		getKeyInTranslation,
	} from "@hitit/hub/src/v7/utils/";
	import { onlyNumberHandler } from "@hitit/hub/src/v7/utils";
	//Types
	import type StoreItem from "../../../class/StoreItem/StoreItem";
	import type OptionCategory from "../../../class/StoreItem/OptionCategory";
	//Props
	export let item: StoreItem;
	export let optionCollections: OptionCategory[];
	//Constants
	const mandatoryErrorKey = "global.product.error.mandatory";
	const onlyNumberErrorKey = "global.product.error.only_number";
	const onlyPositiveNumberErrorKey =
		"global.product.error.only_positive_number";
	const onlyIntegerNumberErrorKey = "global.product.error.only_interger_number";
	const dispatch = createEventDispatcher();
	//Variables
	let price = "";
	let name = "";
	let inventory = "";
	let description = "";
	let options: OptionCategory[];
	//Error
	let nameError = "";
	let priceError = "";
	let stockError = "";
	let placeEnabled = false;
	//Functions
	function clickEventHandler() {
		const _price = Number(price);
		const _inventory = Number(inventory);
		priceError = isNaN(_price)
			? onlyNumberErrorKey
			: _price < 0
			? onlyPositiveNumberErrorKey
			: !Number.isInteger(_price)
			? onlyIntegerNumberErrorKey
			: price === ""
			? mandatoryErrorKey
			: "";
		stockError =
			inventory !== "" && isNaN(_inventory)
				? onlyNumberErrorKey
				: _inventory < 0
				? onlyPositiveNumberErrorKey
				: !Number.isInteger(_inventory)
				? onlyIntegerNumberErrorKey
				: "";
		nameError = name === "" ? mandatoryErrorKey : "";

		if (priceError || stockError || nameError) {
			return;
		}

		const oldTranslation = findTrnaslation(item.getTranslation(), "ko");
		const translation = oldTranslation
			? TranslationDTO.convinienceConstructor(oldTranslation)
			: new TranslationDTO(name);
		translation.name = name;
		item.inventory = _inventory;
		item.price = _price;
		item.setTranslation(translation);
		item.placeEnabled = placeEnabled;
		dispatch("EDIT_ITEM_DONE", item);
	}
	function init(item: StoreItem) {
		price = String(item.price);
		name = getKeyInTranslation(item.getTranslation(), "name", "ko") || "";
		inventory = item.getStockAvailable() ? String(item.getStock()) : "";
		description =
			getKeyInTranslation(item.getTranslation(), "description", "ko") || "";
		options = item.getOptionCollectionDTOs();
		nameError = "";
		priceError = "";
		stockError = "";
		placeEnabled = item.placeEnabled;
	}

	function itemHasThisOption(
		option: OptionCategory,
		optionsOfItem: OptionCategory[]
	) {
		const hasOption = optionsOfItem.find((v) => option.getId() === v.getId());
		return Boolean(hasOption);
	}

	function optionClickHandler(option: OptionCategory) {
		if (itemHasThisOption(option, options)) {
			// delete Option
			options = options.filter((v) => v.getId() !== option.getId());
			item.deleteOption(option.getId() || "");
		} else {
			options = [...options, option];
			item.setOption(option);
		}
	}

	function barcodeHandler(node: HTMLCanvasElement, item: StoreItem) {
		//test code
		// if (true) {
		// 	jsbarcode(node, "123456789012", {
		// 		background: "#f0f0f4",
		// 		height: 61.5,
		// 		displayValue: false,
		// 		lineColor: "#bcbfc3",
		// 	});
		// }

		if (item.getBarcodeAvailable() && item.getBarcode()) {
			jsbarcode(node, item.getBarcode() || "", {
				background: "#f0f0f4",
				height: 61.5,
				displayValue: false,
				lineColor: "#bcbfc3",
			});
		}

		return {
			update(item: StoreItem) {
				if (item.getBarcodeAvailable() && item.getBarcode()) {
					jsbarcode(node, item.getBarcode() || "", {
						background: "#f0f0f4",
						height: 61.5,
						displayValue: false,
						lineColor: "#bcbfc3",
					});
				}
			},
		};
	}

	function barcodeDeleteHandler() {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.category.you_want_to_delete_barcode",
				accept: () => {
					// item.deleteBarcode()
					$popup = undefined;
					item = item;
				},
				cancel: () => ($popup = undefined),
			},
		};
	}
	function numberHandler(inputName: "price" | "inventory") {
		const callback = (_: HTMLElement, v: string) => {
			if (inputName === "price") price = v;
			else inventory = v;
		};

		return (e: Event) => {
			const target = e.target as HTMLInputElement;
			onlyNumberHandler(target, callback);
		};
	}
	//Do
	$: init(item);

</script>

<div class="container" id="storeitem-editviewer">
	<Card noPadding>
		<div class="wrapper">
			<div class="input-wrapper">
				<ProductComposite
					inputMode
					inputId={'editviewer-storeitem-name'}
					placeholder={$t('global.require_input')}
					name={$t('global.product.name')}
					error={nameError !== ''}
					errorMessage={$t(nameError)}
					bind:value={name} />
				<ProductComposite
					inputMode
					numeric
					inputId={'editviewer-storeitem-price'}
					placeholder={$t('global.require_input')}
					error={priceError !== ''}
					errorMessage={$t(priceError)}
					name={$t('global.product.price')}
					on:input={numberHandler("price")}
					value={price} />
				<ProductComposite
					numeric
					inputMode
					name={$t('global.product.description')}
					on:input={numberHandler("inventory")}
					value={inventory}>
					<div class="textarea" slot="input">
						<TextContentArea bind:value={description} />
					</div>
				</ProductComposite>
				<ProductComposite
					numeric
					inputMode
					name={$t('global.product.add_option')}>
					<div slot="input" class="options">
						{#each optionCollections as option}
							<div class="option" on:click={() => optionClickHandler(option)}>
								<div class="checkbox">
									<DefaultCheckbox
										checkedFill={'#6d6f76'}
										checked={itemHasThisOption(option, options)} />
								</div>
								<span
									class:hasOption={itemHasThisOption(option, options)}
									class="option-name">{getKeyInTranslation(option.getTranslations(), 'name', 'ko')}</span>
							</div>
						{/each}
					</div>
				</ProductComposite>
				<ProductComposite
					numeric
					inputMode
					error={stockError !== ''}
					errorMessage={$t(stockError)}
					name={$t('global.product.stock')}
					bind:value={inventory} />
				<ProductComposite numeric inputMode name={$t('global.product.barcode')}>
					<div slot="input">
						{#if item.getBarcodeAvailable()}
							<div class="barcode-wrapper">
								<div class="barcode">
									<canvas use:barcodeHandler={item} width="100" height="100" />
									<p class="barcode-number">{item.getBarcode()}</p>
								</div>
								<img
									class="delete"
									src="/img/icons/deleteIcon.png"
									alt="delete barcode"
									on:click={barcodeDeleteHandler} />
							</div>
						{:else}
							<div class="non-barcode">
								<div class="icon">
									<PopperIcon fill={'#bcbfc3'} />
								</div>
								<p>
									{$t('global.product.category.barcode_registration_use_hub')}
								</p>
							</div>
						{/if}
					</div>
				</ProductComposite>
				<ProductComposite
					numeric
					inputMode
					name={$t('global.product.place_enabled')}
					spaceBetween>
					<div slot="input" class="place-checkbox">
						<DefaultSwitch bind:checked={placeEnabled} />
					</div>
				</ProductComposite>
			</div>
			<div class="button">
				<DefaultButton
					on:click={clickEventHandler}
					fitmode				
					id="editviewer-storeitem-savebutton">
					<span>{$t('global.save')}</span>
				</DefaultButton>
			</div>
		</div>
	</Card>
</div>

<style lang="scss">
	@import "../../../../variables.scss";

	.container {
		display: flex;
		width: 100%;
		flex-direction: column;
	}

	.input-wrapper,
	.wrapper {
		display: flex;
		flex-direction: column;
		width: 100%;
	}

	.input-wrapper {
		gap: 20px;
	}

	.wrapper {
		padding: 20px 10px;
		gap: 20px;
	}

	.button {
		height: 32px;
	}

	span {
		color: white;
		font-weight: 500;
		@include heightRelativeToFontSize(16);
	}

	.textarea {
		width: 100%;
		height: 80px;
	}

	.options {
		display: flex;
		flex-direction: column;
		gap: 10px;

		.option {
			display: flex;
			align-items: center;
			gap: 10px;
			color: #bcbfc3;

			&:hover {
				cursor: pointer;
			}

			.checkbox {
				width: 30px;
				height: 30px;
			}
			.option-name {
				color: #bcbfc3;
				font-weight: 200;

				&.hasOption {
					color: #46474b;
					font-weight: 500;
				}
			}
		}
	}

	.barcode-wrapper {
		display: flex;
		width: 100%;
		gap: 20px;
		.barcode {
			width: 100%;
			height: 136px;
			max-width: 249px;
			background-color: #f0f0f4;
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 5px;

			p {
				font-weight: 500;
				color: #46474b;
			}
		}
		.delete {
			width: 31px;
			height: 31px;

			&:hover {
				cursor: pointer;
			}
		}
	}

	.non-barcode {
		display: flex;
		align-items: center;
		gap: 11px;
		.icon {
			width: 24px;
			height: 24px;
		}

		p {
			color: #bcbfc3;
		}
	}
	@include DesktopSize {
		.wrapper {
			padding: 30px;
		}

		span {
			@include heightRelativeToFontSize(20);
		}

		.button {
			height: 40px;
		}
		.textarea {
			font-size: 1.25rem;
			line-height: 30px;
		}

		.barcode-wrapper {
			gap: 10px;
			.barcode {
				max-width: 281px;
				p {
					font-size: 1.25rem;
				}
			}

			.delete {
				width: 26px;
				height: 26.2px;
			}
		}

		.non-barcode {
			p {
				font-size: 1.25rem;
			}
		}
	}

</style>
